<script setup>
import DefaultButton from "@/components/onsiteEditor/components/buttons/DefaultButton.vue";
import {computed, ref} from "vue";
import ErrorMessages from "@/components/global/ErrorMessages.vue";
import {useMutation} from "@tanstack/vue-query";
import axios from "axios";
import SweetToast from "@/SweetToast";
import {useStore} from "vuex";
import YesNoCheckboxes from "@/components/onsiteEditor/components/forms/inputs/YesNoCheckboxes.vue";
import AssistedInstallationConfirm from "@/components/onsiteEditor/components/installation/modals/AssistedInstallationConfirm.vue";
import {default as GlobalStyledButton} from "@/components/global-themed/buttons/DefaultButton.vue";

defineProps({
  usingGlobalStyling: { // used to use newer styling when embedding this via ai-onboarding flow
    type: Boolean,
    required: false,
    default: false,
  },
});

const emit = defineEmits(['nextStep', 'back']);

const adminUrl = ref('');
const cmsLogin = ref('');
const cmsPass = ref('');
const cachePluginsEnabled = ref(false);
const cachePlugins = ref('');
const twoFaEnabled = ref(false);
const twoFaInfo = ref('');
const store = useStore();
const user = computed(() => store.getters['user/user']);
const showConfirm = ref(false);

const {error, isSuccess, isPending: isSubmitting, ...mutation} = useMutation(({
  mutationFn: async (data) => await axios.post('/onsite-optimizer/assisted-install', data),
  onMutate: async () => {
    window.mixpanel('Onsite Optimizer: Install Myself Submit', {
      website_id: user.value?.active_website?.id,
    })
  },
  onSuccess: async () => {
    await store.dispatch('user/getUser');

    SweetToast.fire({
      icon: 'success',
      title: "Pixel Installation Request Received",
      html: "You’ll receive a confirmation once it’s done, usually within 1 business day."
    });

    emit('back');
  },
  onError: () => {
    SweetToast.fire({
      icon: 'error',
      title: "Check for errors and try again"
    });
  },
  onSettled: () => {
    showConfirm.value = false;
  },
}));

const errors = computed(() => error.value?.response?.data?.errors);

const matchesHikeEmail = computed(() => user.value?.email === cmsLogin.value);

const triggerConfirm = () => {
  showConfirm.value = true;
}

const handleConfirm = () => {
  mutation.mutate({
    adminUrl: adminUrl.value,
    username: cmsLogin.value,
    password: cmsPass.value,
    cachePluginsEnabled: cachePluginsEnabled.value,
    cachePlugins: cachePlugins.value,
    twoFaEnabled: twoFaEnabled.value,
    twoFaInfo: twoFaInfo.value,
  });
}

const handleCancelConfirm = () => {
  showConfirm.value = false;
}
</script>

<template>
  <form
    action=""
    class="tw-mb-25 tw-flex tw-flex-col tw-rounded-xl tw-bg-white tw-p-8"
    autocomplete="off"
    @submit.prevent="triggerConfirm"
  >
    <div class="tw-mb-5 tw-flex tw-text-2xl tw-font-bold tw-leading-8.5 tw-text-slate-100">
      {{ isSuccess ? 'Pixel Installation Request Received' : 'You’re Busy, Let Us Handle It for You' }}
    </div>
    <div
      v-if="!isSuccess"
      class="tw-flex tw-flex-col tw-gap-6 tw-text-slate-200"
    >
      <p class="tw-pb-2">
        We understand that sharing access can feel sensitive, but rest assured, your website’s security is our top
        priority. Allow our team to take care of the setup, saving you time and ensuring everything is configured
        correctly—so we can start optimizing your site without delay.
      </p>
      <p class="tw-pb-4">
        Simply fill out the form below, <span class="tw-font-bold">including your CMS/website login credentials</span>,
        and we’ll securely access your website to install the Onsite Optimizer pixel. A
        member of our team will confirm once it’s done, usually within the next working day.
      </p>
      <div>
        <label
          for="adminUrl"
          class="tw-block tw-w-full"
        >Admin URL:</label>
        <input
          id="adminUrl"
          v-model="adminUrl"
          class="tw-block tw-w-full tw-rounded-xl tw-bg-[#f7f5f2] tw-p-4"
          type="url"
          placeholder="https://example.com/wp-admin"
        >
        <ErrorMessages :errors="errors?.adminUrl ?? []" />
      </div>
      <div>
        <label
          for="cmsLogin"
          class="tw-block tw-w-full"
        >CMS Admin Username (Only used for installation, kept secure):</label>
        <input
          v-model="cmsLogin"
          readonly
          onfocus="this.removeAttribute('readonly');"
          class="tw-block tw-w-full tw-rounded-xl tw-bg-[#f7f5f2] tw-p-4"
          placeholder="CMS Admin Username"
        >
        <small
          v-if="matchesHikeEmail"
          class="tw-text-everest"
        >
          It looks like you’ve added your Hike email, is this correct?
        </small>
        <ErrorMessages :errors="errors?.username ?? []" />
      </div>
      <div>
        <label
          for="cmsPass"
        >CMS Admin Password (Securely accessed, never stored):</label>
        <input
          v-model="cmsPass"
          readonly
          onfocus="this.removeAttribute('readonly');"
          class="tw-block tw-w-full tw-rounded-xl tw-bg-[#f7f5f2] tw-p-4"
          type="password"
          placeholder="********"
        >
        <ErrorMessages :errors="errors?.password ?? []" />
      </div>
      <div class="tw-flex tw-flex-col tw-gap-2">
        <label
          for="cachePluginsEnabled"
        >Are you aware of any caching plugins? (This helps us ensure smooth setup)</label>
        <YesNoCheckboxes v-model="cachePluginsEnabled" />
        <ErrorMessages :errors="errors?.cachePluginsEnabled ?? []" />
      </div>
      <div v-show="cachePluginsEnabled">
        <label
          for="cachePlugins"
        >Cache Plugins:</label>
        <input
          id="cachePlugins"
          v-model="cachePlugins"
          class="tw-block tw-w-full tw-rounded-xl tw-bg-[#f7f5f2] tw-p-4"
          placeholder="e.g. WP Cache, W3 Total Cache, etc"
        >
        <ErrorMessages :errors="errors?.cachePlugins ?? []" />
      </div>

      <div class="tw-flex tw-flex-col tw-gap-2">
        <label
          for="cachePluginsEnabled"
        >
          Do you have two factor authentication (2FA) enabled when you login to your website?
        </label>
        <YesNoCheckboxes v-model="twoFaEnabled" />
        <ErrorMessages :errors="errors?.twoFaEnabled ?? []" />
      </div>
      <div v-show="twoFaEnabled">
        <label
          for="twoFaInfo"
        >Confirm days/times that works with you for us to install OSO:</label>
        <input
          id="twoFaInfo"
          v-model="twoFaInfo"
          class="tw-block tw-w-full tw-rounded-xl tw-bg-[#f7f5f2] tw-p-4"
          placeholder="e.g. Morning on Mon-Fri, etc"
        >
        <ErrorMessages :errors="errors?.twoFaInfo ?? []" />
      </div>
    </div>
    <div v-if="isSuccess">
      <p class="tw-pb-2">
        Thank you! We’ve received your request to install the Onsite Optimizer pixel. A member of our team will securely
        access your site and complete the installation.
      </p>
      <p>
        You’ll receive a confirmation once it’s done, usually within 1
        business day.
      </p>
    </div>

    <div
      v-if="!isSuccess"
      class="tw-mt-5 tw-flex tw-gap-4"
      :class="{
        'tw-justify-between' : usingGlobalStyling,
      }"
    >
      <template
        v-if="usingGlobalStyling"
      >
        <GlobalStyledButton
          variant="primary-inverted"
          @click="emit('back')"
        >
          Back
        </GlobalStyledButton>

        <GlobalStyledButton
          v-if="usingGlobalStyling"
          type="submit"
        >
          Submit
        </GlobalStyledButton>
      </template>

      <template v-else>
        <DefaultButton
          class="!tw-w-[280px]"
          type="submit"
        >
          Submit
        </DefaultButton>
        <DefaultButton
          class="!tw-w-[280px]"
          variant="base"
          @click.prevent="emit('back')"
        >
          Back
        </DefaultButton>
      </template>
    </div>

    <AssistedInstallationConfirm
      v-if="showConfirm"
      :loading="isSubmitting"
      :using-global-styling="usingGlobalStyling"
      @confirm="handleConfirm"
      @cancel="handleCancelConfirm"
    />
  </form>
</template>
